import React from "react";
import { CBadge } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";

/**
 * Job status color mapping - shared across all pages displaying job status
 */
const JOB_STATUS_COLORS: Record<string, string> = {
  queued: "secondary",
  claimed: "info",
  running: "primary",
  completed: "success",
  failed: "danger",
  cancelled: "warning",
};

// Paused is amber like cancelled, but in the subtle tone so the two stay apart at a glance.
const JOB_STATUS_CLASSES: Record<string, string> = {
  paused: "JobStatusBadgePaused bg-warning-subtle text-warning-emphasis border border-warning",
};

interface Props {
  status: string | undefined;
  runningState?: string;
}

/**
 * Renders a CoreUI badge for job status with appropriate color and spinner for running jobs.
 * For running jobs with progress, shows the running_state (e.g., "523/1000")
 */
export const JobStatusBadge: React.FC<Props> = ({ status, runningState }) => {
  if (!status) {
    return (
      <CBadge
        color="secondary"
        className="d-flex align-items-center"
        style={{ width: "fit-content" }}
      >
        N/A
      </CBadge>
    );
  }

  const statusClass = JOB_STATUS_CLASSES[status];
  const color = statusClass ? undefined : JOB_STATUS_COLORS[status] || "secondary";
  const isRunning = status === "running";

  // Build status text with optional progress for running jobs
  let statusText = status.toUpperCase();
  if (isRunning && runningState) {
    statusText = runningState;
  }

  return (
    <CBadge
      color={color}
      className={`d-flex align-items-center${statusClass ? ` ${statusClass}` : ""}`}
      style={{ width: "fit-content" }}
    >
      {isRunning ? <CSpinnerText text={statusText} /> : statusText}
    </CBadge>
  );
};
